import { fabric } from 'fabric';
import { useSnackbar } from 'notistack';

import { IFRAME_WIDGETS } from '../../config';
import { useTooling } from '../../lib/FabricJs';
import handleAddWebSite from '../../lib/FabricJs/utils/handleWebSite';
import { useHandleHistory } from '../../lib/FabricJs/hooks/useHandleHistory';
import handleWorldClockAndDate from '../../lib/FabricJs/utils/handleWorldClockAndDate';
import {
  handleVideoFromFabricObject,
  MAX_VIDEOS_PER_SLIDE,
  VideoModeEnum,
} from '../../lib/FabricJs/utils/handleVideo';
import { disableTextResizeHandles } from '../../utils/textControls';
import { applyQrControls } from '../../lib/FabricJs/utils/qrObject';
import { useSnack } from '../../../../util/useSnack';

const TEXT_OBJECT_TYPES = ['text', 'i-text', 'textbox'];

const { WORLD_CLOCK, DATE, IFRAME } = IFRAME_WIDGETS;

// Autosave only saves a canvas the slide was loaded into; saving any other
// canvas (e.g. a fresh, empty one) would overwrite the slide.
const loadedCanvases = new WeakSet<fabric.Canvas>();
export const isSlideLoaded = (canvas: fabric.Canvas) =>
  loadedCanvases.has(canvas);

// The browser only starts loading a web font when the canvas first draws
// with it, so text is measured and drawn with a fallback font. Rendering is
// on demand, so nothing redraws it once the font arrives: re-measure (Fabric
// caches character widths per font) and redraw when the fonts are ready.
const refreshTextWhenFontsLoad = (canvas: fabric.Canvas) => {
  const textObjects = canvas
    .getObjects()
    .filter((object) =>
      TEXT_OBJECT_TYPES.includes(object.type || ''),
    ) as fabric.IText[];
  const families = Array.from(
    new Set(textObjects.map((object) => object.fontFamily).filter(Boolean)),
  ) as string[];
  if (!families.length) return;
  Promise.all(
    families.map((family) =>
      document.fonts.load(`16px "${family}"`).catch(() => []),
    ),
  ).then(() => {
    if (!canvas.getContext()) return;
    families.forEach((family) => fabric.util.clearFabricFontCache(family));
    textObjects.forEach((object) => {
      object.initDimensions();
      object.setCoords();
    });
    canvas.requestRenderAll();
  });
};

export const useLoadJSON = () => {
  const { handleHistory, resetHistoryTo } = useHandleHistory();
  const { showErrSnack } = useSnack();
  const { enqueueSnackbar } = useSnackbar();
  const {
    slidesProps: { updatedKvs },
  } = useTooling();

  const loadJSON = (canvas: fabric.Canvas, projectData: common.ProjectData) => {
    for (const object of projectData?.objects) {
      if (object?.data?.type === 'video') {
        object.src = '';
      } else if (object?.data?.type === 'kvs' && updatedKvs) {
        const kvsId = object?.data?.kvsId;
        if (updatedKvs[kvsId]) {
          object.text = updatedKvs[kvsId];
        }
      }
    }
    const expectedObjects = (projectData?.objects || []).filter(
      (object: any) => object?.type,
    ).length;
    // Slides made before the one-video limit keep their videos.
    const videos = (projectData?.objects || []).filter(
      (object: any) => object?.data?.type === 'video',
    ).length;
    canvas.loadFromJSON(projectData, async () => {
      // Fabric silently drops objects that fail to load (e.g. an image URL
      // that errors). Saving that canvas would delete them from the slide,
      // so saving stays disabled until the slide is reloaded.
      if (canvas.getObjects().length < expectedObjects) {
        showErrSnack(
          'Some items on this slide failed to load. Reload the page before editing; changes will not be saved.',
        );
      } else {
        // Marked before videos load: a video whose metadata never arrives
        // would otherwise block saving for the whole session.
        loadedCanvases.add(canvas);
        if (videos > MAX_VIDEOS_PER_SLIDE) {
          enqueueSnackbar(
            `This slide has ${videos} videos, but a slide can have one. You can still save it; delete the extra videos when you can.`,
            { variant: 'warning', autoHideDuration: 8000 },
          );
        }
      }
      for (const obj of canvas.getObjects()) {
        if (TEXT_OBJECT_TYPES.includes(obj.type || '')) {
          disableTextResizeHandles(obj);
        }
        applyQrControls(obj);
        const params = { canvas: canvas, obj, isTime: true, handleHistory };
        switch (obj?.data?.type) {
          case 'video':
            await handleVideoFromFabricObject(
              canvas,
              obj,
              VideoModeEnum.Thumbnail,
            );
            // @ts-ignore;
            obj.isDelete = true; //Identify duplicate objects.
            canvas._objects.splice(canvas._objects.indexOf(obj), 1);
            break;
          case IFRAME:
            handleAddWebSite(canvas, obj?.data.src, handleHistory, obj);
            break;
          case WORLD_CLOCK:
            handleWorldClockAndDate({ ...params });
            break;
          case DATE:
            params.isTime = false;
            handleWorldClockAndDate({ ...params });
            break;
          default:
            canvas?.bringToFront(obj);
        }
      }
      canvas.hoverCursor = 'default';
      // Adding the loaded objects records history entries; undoing those
      // would remove the slide's own content (and autosave it).
      resetHistoryTo(canvas);
      refreshTextWhenFontsLoad(canvas);
    });
    canvas.discardActiveObject();
  };
  return { loadJSON };
};
